import { FC } from 'react';
import { useQuery } from 'react-query';
import { Form } from '@/components/form';
import FormButtons from '@/components/form/form-buttons';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { QueryKey } from '@/lib/query-client';
import { getScopedPolicyWithParents, patchPolicy } from '@/lib/services/policy.service';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { cloneDeep } from 'lodash';
import { FieldError } from '@/lib/services';
import FormSection from '@/components/form/form-section';
import { Grid } from '@mui/material';
import InputControl from '@/components/form/input-control';
import FormLabel from '@/components/form-label';
import AutoScopeFormSection from './auto-scope-form-section';
import NewServices from '@/components/form/new-services';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['publicServices', 'allGroups'];
const formModel = new PolicyModel();

interface Props {
  policyId: string;
}

const ScopeForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy } = useFeature();

  const query = useQuery([QueryKey.ScopedPolicyWithParents, policyId], async () =>
    getScopedPolicyWithParents(policyId)
  );

  const { refetch, data } = query;
  const [policy] = data || [];

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const updateValues = cloneDeep(values);

      const { patched, error, fieldErrors } = await patchPolicy(
        policyId,
        updateValues.propSlice(fields)
      );

      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        refetch();
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const { values } = formHandle;
  const { allGroups, allServices } = values;
  const hasAutoScope = allGroups || allServices;
  const isDefaultPolicy = !!policy?.isDefault;

  return (
    <Form formHandle={formHandle} query={query}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      {isDefaultPolicy && (
        <NewServices formHandle={formHandle} canChangePolicy={canChangePolicy} policy={policy} />
      )}

      <AutoScopeFormSection formHandle={formHandle} readonly={!canChangePolicy} policy={policy!} />

      <FormSection
        title="Individual Group Scoping"
        scrollId="group-scoping"
        disabled={hasAutoScope}
      >
        <FormSectionSubtitle>
          Use the table below to choose which groups are scoped to this policy.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="groups"
              label="Groups"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="groups-list-toggle"
              disabled={hasAutoScope}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Individual Service Block List"
        scrollId="blocked-services"
        disabled={hasAutoScope}
      >
        <FormSectionSubtitle>
          Use the table below to choose which services will be blocked by this policy.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="publicServices"
              label="Public Services"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="public-service-acl"
              aclVariant="block"
              disabled={hasAutoScope}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Individual Service Allow List"
        scrollId="allowed-services"
        disabled={hasAutoScope}
      >
        <FormSectionSubtitle>
          Use the table below to choose which services will be allowed by this policy.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Allowed services can be either monitored or protected. Protected services are governed
            by the sensitive data settings for this policy. Monitored service activity will be
            logged with no further action taken.
          </Grid>
          <Grid item xs={12}>
            <InputControl
              name="publicServices"
              label="Public Services"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="public-service-acl"
              aclVariant="allow"
              disabled={hasAutoScope}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Restrict Service Scoping to Non-business Licenses"
        scrollId="auto-scope"
        disabled={allGroups}
      >
        <FormSectionSubtitle>
          When this setting is enabled, service scoping will only apply when the license detected in
          the request is non-business.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <FormLabel disabled={allServices}>Enabled</FormLabel>
            <InputControl
              name="publicServices.nonBusinessLicense"
              type="on-off-toggle-button"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              withNotSet={false}
              disabled={allGroups}
            />
          </Grid>
          <Grid item xs={7}></Grid>
        </Grid>
      </FormSection>

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default ScopeForm;
